import React, { useState, useEffect } from 'react';
import { Modal, Typography, Button, Space, Tag, Input, Select, Divider, Empty, message } from 'antd';
import {
    CloseOutlined,
    SaveOutlined,
    PlusOutlined,
    DeleteOutlined,
    DragOutlined,
    LayoutOutlined,
    BuildOutlined,
    MobileOutlined,
    TabletOutlined,
    DesktopOutlined
} from '@ant-design/icons';

const { Text, Title } = Typography;

/**
 * TemplateEditor - Full-page template composition from organisms
 */
const TemplateEditor = ({
    visible,
    template,
    designKit,
    canvasId,
    onClose,
    onSave
}) => {
    const [loading, setLoading] = useState(false);
    const [localTemplate, setLocalTemplate] = useState({
        name: '',
        type: '',
        device: 'mobile',
        sections: [], // Array of { organismId, order, spacing }
        width: 375,
        height: 812
    });

    // Initialize from template prop
    useEffect(() => {
        if (template) {
            setLocalTemplate({
                name: template.name || '',
                type: template.type || '',
                device: template.devices?.[0] || 'mobile',
                sections: template.children || [],
                width: template.defaultWidth || 375,
                height: template.defaultHeight || 812
            });
        } else {
            setLocalTemplate({
                name: '',
                type: '',
                device: 'mobile',
                sections: [],
                width: 375,
                height: 812
            });
        }
    }, [template, visible]);

    const devicePresets = {
        mobile: { width: 375, height: 812, label: 'Mobile', icon: <MobileOutlined /> },
        tablet: { width: 768, height: 1024, label: 'Tablet', icon: <TabletOutlined /> },
        desktop: { width: 1440, height: 900, label: 'Desktop', icon: <DesktopOutlined /> }
    };

    // Get available organisms
    const availableOrganisms = designKit?.components?.organisms || [];

    const handleDeviceChange = (device) => {
        const preset = devicePresets[device];
        setLocalTemplate(prev => ({
            ...prev,
            device,
            width: preset.width,
            height: preset.height
        }));
    };

    const handleAddSection = (organismId) => {
        setLocalTemplate(prev => ({
            ...prev,
            sections: [
                ...prev.sections,
                {
                    componentId: organismId,
                    order: prev.sections.length,
                    spacing: { top: 'space-0', bottom: 'space-0' }
                }
            ]
        }));
    };

    const handleRemoveSection = (index) => {
        setLocalTemplate(prev => ({
            ...prev,
            sections: prev.sections.filter((_, i) => i !== index)
        }));
    };

    const handleMoveSection = (index, direction) => {
        const newIndex = index + direction;
        if (newIndex < 0 || newIndex >= localTemplate.sections.length) return;

        setLocalTemplate(prev => {
            const sections = [...prev.sections];
            [sections[index], sections[newIndex]] = [sections[newIndex], sections[index]];
            return { ...prev, sections };
        });
    };

    const handleSave = async () => {
        if (!localTemplate.name?.trim()) {
            message.error('Template name is required');
            return;
        }
        if (!localTemplate.type?.trim()) {
            message.error('Template type is required');
            return;
        }
        if (localTemplate.sections.length === 0) {
            message.error('Add at least one section');
            return;
        }

        setLoading(true);
        try {
            await onSave?.({
                name: localTemplate.name,
                type: localTemplate.type,
                devices: [localTemplate.device],
                defaultWidth: localTemplate.width,
                defaultHeight: localTemplate.height,
                children: localTemplate.sections
            });
            message.success('Template saved');
            onClose();
        } catch (error) {
            message.error(error.message || 'Failed to save template');
        } finally {
            setLoading(false);
        }
    };

    const getOrganismById = (id) => {
        return availableOrganisms.find(o => o.id === id);
    };

    return (
        <Modal
            open={visible}
            onCancel={onClose}
            footer={null}
            closable={false}
            width="90vw"
            style={{ top: 20, maxWidth: 1400 }}
            styles={{
                body: { padding: 0, background: '#0d1117' },
                content: { background: '#0d1117', borderRadius: 12 }
            }}
        >
            <div style={{ display: 'flex', height: '85vh', maxHeight: 900 }}>

                {/* LEFT: Preview Panel */}
                <div style={{
                    flex: 1,
                    background: '#161b22',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRight: '1px solid #30363d'
                }}>
                    {/* Preview Header */}
                    <div style={{
                        padding: '16px 24px',
                        borderBottom: '1px solid #30363d',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <LayoutOutlined style={{ color: '#58a6ff', fontSize: 16 }} />
                            <Text style={{ color: '#e6edf3', fontWeight: 600 }}>Template Preview</Text>
                        </div>
                        <div style={{ display: 'flex', gap: 8 }}>
                            {Object.entries(devicePresets).map(([key, preset]) => (
                                <div
                                    key={key}
                                    onClick={() => handleDeviceChange(key)}
                                    style={{
                                        padding: '6px 12px',
                                        background: localTemplate.device === key ? '#58a6ff20' : 'transparent',
                                        border: `1px solid ${localTemplate.device === key ? '#58a6ff' : '#30363d'}`,
                                        borderRadius: 6,
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 6
                                    }}
                                >
                                    <span style={{ color: localTemplate.device === key ? '#58a6ff' : '#8b949e' }}>
                                        {preset.icon}
                                    </span>
                                    <Text style={{ color: localTemplate.device === key ? '#e6edf3' : '#8b949e', fontSize: 12 }}>
                                        {preset.label}
                                    </Text>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Preview Canvas */}
                    <div style={{
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: 24,
                        overflow: 'auto'
                    }}>
                        <div style={{
                            width: Math.min(localTemplate.width, 500),
                            height: Math.min(localTemplate.height, 700),
                            background: '#0d1117',
                            border: '2px solid #30363d',
                            borderRadius: 12,
                            overflow: 'hidden',
                            display: 'flex',
                            flexDirection: 'column'
                        }}>
                            {localTemplate.sections.length === 0 ? (
                                <div style={{
                                    flex: 1,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}>
                                    <Empty
                                        description={<Text style={{ color: '#8b949e' }}>Add sections to preview</Text>}
                                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                                    />
                                </div>
                            ) : (
                                localTemplate.sections.map((section, idx) => {
                                    const organism = getOrganismById(section.componentId);
                                    return (
                                        <div
                                            key={idx}
                                            style={{
                                                padding: '16px 12px',
                                                borderBottom: idx < localTemplate.sections.length - 1 ? '1px dashed #30363d' : 'none',
                                                background: '#21262d'
                                            }}
                                        >
                                            <div style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 8
                                            }}>
                                                <BuildOutlined style={{ color: '#a371f7', fontSize: 12 }} />
                                                <Text style={{ color: '#e6edf3', fontSize: 12 }}>
                                                    {organism?.name || section.componentId}
                                                </Text>
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </div>
                </div>

                {/* RIGHT: Editor Panel */}
                <div style={{ width: 380, display: 'flex', flexDirection: 'column' }}>
                    {/* Header */}
                    <div style={{
                        padding: '16px 20px',
                        borderBottom: '1px solid #30363d',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <Title level={5} style={{ margin: 0, color: '#e6edf3' }}>
                            {template ? 'Edit Template' : 'New Template'}
                        </Title>
                        <Button
                            type="text"
                            icon={<CloseOutlined />}
                            onClick={onClose}
                            style={{ color: '#8b949e' }}
                        />
                    </div>

                    {/* Editor Content */}
                    <div style={{ flex: 1, overflow: 'auto', padding: 20 }}>
                        {/* Name & Type */}
                        <div style={{ marginBottom: 16 }}>
                            <Text style={{ color: '#8b949e', fontSize: 11, display: 'block', marginBottom: 6 }}>
                                TEMPLATE NAME
                            </Text>
                            <Input
                                value={localTemplate.name}
                                onChange={(e) => setLocalTemplate(prev => ({ ...prev, name: e.target.value }))}
                                placeholder="e.g., Home Screen"
                                style={{
                                    background: '#161b22',
                                    border: '1px solid #30363d',
                                    borderRadius: 6
                                }}
                            />
                        </div>

                        <div style={{ marginBottom: 16 }}>
                            <Text style={{ color: '#8b949e', fontSize: 11, display: 'block', marginBottom: 6 }}>
                                TYPE (camelCase)
                            </Text>
                            <Input
                                value={localTemplate.type}
                                onChange={(e) => setLocalTemplate(prev => ({ ...prev, type: e.target.value }))}
                                placeholder="e.g., homeScreen"
                                style={{
                                    background: '#161b22',
                                    border: '1px solid #30363d',
                                    borderRadius: 6
                                }}
                            />
                        </div>

                        <Divider style={{ margin: '20px 0', borderColor: '#30363d' }} />

                        {/* Sections */}
                        <div style={{ marginBottom: 16 }}>
                            <Text style={{ color: '#8b949e', fontSize: 11, display: 'block', marginBottom: 12 }}>
                                SECTIONS ({localTemplate.sections.length})
                            </Text>

                            {/* Section List */}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
                                {localTemplate.sections.map((section, idx) => {
                                    const organism = getOrganismById(section.componentId);
                                    return (
                                        <div
                                            key={idx}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 8,
                                                padding: '10px 12px',
                                                background: '#161b22',
                                                border: '1px solid #30363d',
                                                borderRadius: 6
                                            }}
                                        >
                                            <DragOutlined style={{ color: '#484f58', cursor: 'move' }} />
                                            <BuildOutlined style={{ color: '#a371f7' }} />
                                            <Text style={{ color: '#e6edf3', flex: 1, fontSize: 13 }}>
                                                {organism?.name || section.componentId}
                                            </Text>
                                            <Button
                                                type="text"
                                                size="small"
                                                icon={<DeleteOutlined />}
                                                onClick={() => handleRemoveSection(idx)}
                                                style={{ color: '#8b949e' }}
                                            />
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Add Section Dropdown */}
                            <Select
                                placeholder="Add organism section..."
                                style={{ width: '100%' }}
                                options={availableOrganisms.map(o => ({
                                    value: o.id,
                                    label: (
                                        <Space>
                                            <BuildOutlined style={{ color: '#a371f7' }} />
                                            {o.name}
                                        </Space>
                                    )
                                }))}
                                onSelect={(value) => handleAddSection(value)}
                                value={null}
                            />
                        </div>

                        {/* Dimensions */}
                        <Divider style={{ margin: '20px 0', borderColor: '#30363d' }} />
                        <div style={{ display: 'flex', gap: 12 }}>
                            <div style={{ flex: 1 }}>
                                <Text style={{ color: '#8b949e', fontSize: 10, display: 'block', marginBottom: 4 }}>
                                    WIDTH
                                </Text>
                                <Input
                                    type="number"
                                    value={localTemplate.width}
                                    onChange={(e) => setLocalTemplate(prev => ({ ...prev, width: parseInt(e.target.value) || 0 }))}
                                    size="small"
                                    style={{ background: '#161b22', border: '1px solid #30363d' }}
                                />
                            </div>
                            <div style={{ flex: 1 }}>
                                <Text style={{ color: '#8b949e', fontSize: 10, display: 'block', marginBottom: 4 }}>
                                    HEIGHT
                                </Text>
                                <Input
                                    type="number"
                                    value={localTemplate.height}
                                    onChange={(e) => setLocalTemplate(prev => ({ ...prev, height: parseInt(e.target.value) || 0 }))}
                                    size="small"
                                    style={{ background: '#161b22', border: '1px solid #30363d' }}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Footer */}
                    <div style={{
                        padding: '16px 20px',
                        borderTop: '1px solid #30363d',
                        display: 'flex',
                        gap: 12
                    }}>
                        <Button onClick={onClose} style={{ flex: 1 }}>
                            Cancel
                        </Button>
                        <Button
                            type="primary"
                            icon={<SaveOutlined />}
                            onClick={handleSave}
                            loading={loading}
                            style={{ flex: 1 }}
                        >
                            Save Template
                        </Button>
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default TemplateEditor;
